import React, { FC, ReactNode, RefObject, useRef, useState } from 'react';
import { PostComposerProvider } from 'app/modules/posts/modules/post/components/composer/PostComposerProvider';
import useSetState, { UseSetStateAction } from 'utils/hooks/useSetState';
import { PostComposerFormState } from 'app/modules/posts/modules/post/components/composer/utils/postComposerFormState';
import { useMutation, useQueryClient } from 'react-query';
import PostApi from 'api/post';
import { useToast } from 'app/hooks/useToast';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { queryKey } from 'constants/queryKey';
import { Editor } from '@tiptap/react';
import { ApiClientError } from 'api/client';
import Logger from 'utils/logger';
import UserpilotUtils from 'app/utils/userpilot';
import { PostModalOnClose } from 'app/modules/posts/modules/post/hooks/usePostModal';
import { VideoPlayerRef } from '@kontentino/kontentino-post-previews';
import { unstable_BlockerFunction as BlockerFunction } from 'react-router-dom';
import UnsavedChangesHandler from 'app/components/UnsavedChangesHandler';
import {
  PostComposerFormOptions,
  usePostComposerFormOptions,
} from 'app/modules/posts/modules/post/components/composer/hooks/usePostComposerFormOptions';
import { PostStatus, STATUS } from '@kontentino/kontentino-constants/Posts';
import type { PostDetail } from 'types/PostDetail';
import { useTranslation } from 'react-i18next';
import { canSaveScheduledDateTime } from 'app/modules/posts/utils/scheduledPastDate';
import { useInvalidatePostsQueries } from 'app/modules/posts/hooks/useInvalidatePostsQueries';
import Popup from 'utils/popup';
import { hasAudienceRestrictionValue } from 'app/modules/posts/modules/detail/modules/audienceRestrictions/utils/audienceRestrictions';
import { TranslationSettings } from 'app/modules/posts/modules/translation/TranslationLanguageDropdown';
import { useUser } from 'modules/user/userSelector';
import { getUserFirstTimeFlagStatus } from 'utils/user/firstTimeFlags';
import { FirstTimeFlagKey } from 'types/User';
import { useDispatch } from 'react-redux';
import { UserActions } from 'modules/user/userActions';
import { useG2ReviewTrigger } from 'app/hooks/useG2ReviewTrigger';
import {
  G2ReviewTracker,
  MIN_POST_SAVES_TO_TRIGGER,
} from 'app/utils/g2ReviewTracker';

type Props = {
  children(params: PostComposerProviderState): ReactNode | ReactNode[];
  isEditing: boolean;
  initialFormState: PostComposerFormState;
  onClose: PostModalOnClose;
};

interface SavePostOptions {
  onSuccess?(): void;
  onError?(): void;
  confirmRepublish?: boolean;
  closeAfterSave?: boolean;
}

export type PostComposerProviderState = {
  isEditing: boolean;
  form: PostComposerFormState;
  setForm: UseSetStateAction<PostComposerFormState>;
  isSaving: boolean;
  savePost(options?: SavePostOptions): Promise<PostDetail | undefined>;
  options: PostComposerFormOptions;
  setOptions: UseSetStateAction<PostComposerFormOptions>;
  onTextEditorInit(textEditor: Editor): void;
  onTranslationEditorInit(editor: Editor): void;
  swapTextTranslation(): void;
  videoPlayerRef: RefObject<VideoPlayerRef>;
  translationSettings: Record<number, TranslationSettings>;
  setTranslationSettings: React.Dispatch<
    React.SetStateAction<Record<number, TranslationSettings>>
  >;
};

const PostComposer: FC<Props> = ({
  isEditing,
  children,
  onClose,
  initialFormState,
}) => {
  const queryClient = useQueryClient();
  const textEditor = useRef<Editor>();
  const videoPlayerRef = useRef<VideoPlayerRef>(null);
  const translationEditor = useRef<Editor>();
  const closeAfterSaveRef = useRef(true);

  const toast = useToast();
  const [form, setForm] = useSetState<PostComposerFormState>(initialFormState);
  const [translationSettings, setTranslationSettings] = useState<
    Record<number, TranslationSettings>
  >({});

  const { options, setOptions } = usePostComposerFormOptions();
  const { t } = useTranslation();
  const invalidatePostsRequests = useInvalidatePostsQueries();
  const { accountPlanType } = useSubscriptionInfo();

  const user = useUser();
  const dispatch = useDispatch();
  const { triggerG2Review } = useG2ReviewTrigger();

  const isFirstPost = !getUserFirstTimeFlagStatus(
    user.firstTimeFlags,
    FirstTimeFlagKey.FIRST_POST_CREATED,
  );

  function removePostCache(id: number) {
    try {
      queryClient.removeQueries(queryKey.postDetail(id));
      queryClient.removeQueries(queryKey.postScheduleValidation(id));
      queryClient.removeQueries(queryKey.postCommentsThread());
      queryClient.invalidateQueries(queryKey.posts());
    } catch (e) {
      Logger.error(e);
    }
  }

  const save = useMutation(PostApi.save, {
    async onSuccess(post, variables) {
      UserpilotUtils.track(
        form.id
          ? UserpilotUtils.events.POST_UPDATED
          : post.date === null
          ? UserpilotUtils.events.POST_CREATED_WITHOUT_DATE
          : UserpilotUtils.events.POST_CREATED,
      );
      if (variables.distributeToPageIds.length > 0) {
        const translations = Object.entries(translationSettings).map(
          ([pageId, settings]) => ({
            pageId: Number(pageId),
            ...settings,
          }),
        );

        try {
          await PostApi.distributeGlobalPostToLocalPages({
            postId: post.id,
            pageIds: variables.distributeToPageIds,
            translations,
            planType: accountPlanType,
          });
        } catch (e) {
          Logger.error(e, 'distribute-global-post-to-local-pages');
        }
      }

      function getMessage() {
        let message =
          post.date === null ? t('postSavedToPostIdeas') : t('postSaved');

        if (
          initialFormState.status !== post.status &&
          isEditing &&
          (
            [
              STATUS.WAITING_FOR_INTERNAL_APPROVAL,
              STATUS.AT_CLIENT,
            ] as PostStatus[]
          ).includes(post.status)
        ) {
          message += ` ${t('and')} ${t(
            `workflowStatusMovedTo.${post.status}`,
          )}`;
        }

        return message;
      }

      toast(getMessage(), 'success');

      removePostCache(post.id);
      invalidatePostsRequests();

      const postSaveCount = G2ReviewTracker.incrementPostSaveCount();
      if (postSaveCount === MIN_POST_SAVES_TO_TRIGGER) {
        triggerG2Review();
      }

      if (!isEditing && isFirstPost) {
        dispatch(
          UserActions.setFirstTimeFlag(FirstTimeFlagKey.FIRST_POST_CREATED),
        );
      }

      if (!closeAfterSaveRef.current) {
        return;
      }

      if (!isEditing && isFirstPost) {
        onClose({
          postIdToOpen: post.id,
          isPostCreationDone: true,
          showFirstPostModal: true,
          pageId: post.page.id,
        });
      } else {
        onClose({ postIdToOpen: post.id, isPostCreationDone: true });
      }
    },
    onError(e: ApiClientError) {
      Logger.error(e, 'posts-post-detail-save-post');
      toast(e.userMessage ?? t('somethingWentWrongTryAgain'), 'error');
    },
  });

  function onTextEditorInit(editor: Editor) {
    textEditor.current = editor;
  }

  function onTranslationEditorInit(editor: Editor) {
    translationEditor.current = editor;
  }

  function swapTextTranslation() {
    const prevTranslationText = translationEditor.current?.getHTML() ?? '';
    const prevPostText = textEditor.current?.getHTML() ?? '';

    const options = [false, { preserveWhitespace: true }] as const;
    translationEditor.current?.commands.setContent(prevPostText, ...options);
    textEditor.current?.commands.setContent(prevTranslationText, ...options);
    toast(t('translationsSuccessfullySwapped'));
  }

  async function savePost(
    options?: SavePostOptions,
  ): Promise<PostDetail | undefined> {
    const values = { ...form, confirmRepublish: options?.confirmRepublish };

    if (!values.project?.id) {
      toast(t('contentPlanRequired'), 'warning');
      return undefined;
    }

    // make sure we have the latest text because it's uncontrolled component
    values.text = textEditor.current?.getText() ?? '';
    values.translationText = translationEditor.current?.getText() ?? '';

    if (values.audienceRestrictions) {
      const filteredAudienceRestrictions = Object.fromEntries(
        Object.entries(values.audienceRestrictions).filter(([field, value]) => {
          return hasAudienceRestrictionValue(value);
        }),
      );
      values.audienceRestrictions = filteredAudienceRestrictions;
    }

    const hasLocalPosts = values.distributeToPageIds.length > 0;

    if (hasLocalPosts) {
      const { isConfirmed } = await Popup.confirm({
        title: t('applyChangesToLocalPostsConfirmation.title'),
        text: t('applyChangesToLocalPostsConfirmation.text'),
        confirmButtonText: t(
          'applyChangesToLocalPostsConfirmation.confirmButtonText',
        ),
        cancelButtonText: t(
          'applyChangesToLocalPostsConfirmation.cancelButtonText',
        ),
        customClass: '!tw-max-w-[400px]',
        showCancelButton: true,
        showDenyButton: false,
      });

      if (!isConfirmed) {
        return undefined;
      }
    }

    const canSave = await canSaveScheduledDateTime(
      {
        status: values.status,
        date: values.date,
        time: values.time,
        pageTimezone: values.page?.timezone,
      },
      t,
    );

    if (!canSave) {
      return undefined;
    }

    closeAfterSaveRef.current = options?.closeAfterSave ?? true;

    return save.mutateAsync(values).then(
      (post) => {
        options?.onSuccess?.();
        return post;
      },
      () => {
        options?.onError?.();
        return undefined;
      },
    );
  }

  const providerState: PostComposerProviderState = {
    isEditing,
    isSaving: save.isLoading,
    form,
    setForm,
    savePost,
    options,
    setOptions,
    onTextEditorInit,
    onTranslationEditorInit,
    swapTextTranslation,
    videoPlayerRef,
    translationSettings,
    setTranslationSettings,
  };

  const variant = isEditing ? 'edit' : 'create';

  const shouldBlock: BlockerFunction = ({ currentLocation, nextLocation }) => {
    const nextSearchParams = new URLSearchParams(nextLocation.search);
    const currentSearchParams = new URLSearchParams(currentLocation.search);

    const postCreationComplete = nextSearchParams.get('isPostCreationDone');
    const isComposing = currentSearchParams.get('compose');
    const nextIsComposing = nextSearchParams.get('compose');

    if (postCreationComplete === 'true') {
      return false;
    }

    return isComposing === 'true' && nextIsComposing !== 'true';
  };

  return (
    <UnsavedChangesHandler
      type="post"
      variant={variant}
      shouldBlock={shouldBlock}
      isSubmitting={providerState.isSaving}
      onSubmit={(options) => savePost(options)}
    >
      <PostComposerProvider value={providerState}>
        {children(providerState)}
      </PostComposerProvider>
    </UnsavedChangesHandler>
  );
};

export default PostComposer;
